import { RunReading, RunSnapshot, RunStep, firstValue, printValue, sameIdentifier } from './runs';
import { roleSpec } from './roles';
import { v } from './schema';

/**
 * The structure a procedure repeats its steps over — function × supply
 * voltage, function × temperature — declared once in a report's settings so
 * it can print a results matrix instead of one block per step.
 *
 * A dimension names its values and the words that identify each one in a
 * step's title or path ("Low Beam", "LB"). Where the procedure records the
 * condition as a parameter with one of the identifiers of the dimension's role
 * (a self-describing `signal_name`, `temperature_corner`), that is read first.
 * A dimension can instead take its values from one of the procedure's own
 * lists (`parameter: 'temperatures'`): the entries the run selected are its
 * values, the rest are what the procedure also offers. A name a step quotes
 * ("Nominal Temperature") wins over one it merely mentions.
 * `proposeDimensions` suggests dimensions from a run's step titles.
 */

export interface DimensionValue {
  label: string;
  /** other words that mean this value in a step title, besides the label itself */
  match: string[];
}

export interface Dimension {
  id: string;
  label: string;
  /** a parameter role (roles.ts) whose reading names this dimension's value, e.g. `condition.function` */
  role?: string;
  /** a procedure-level parameter (a choice or list the procedure loops over, e.g. `temperatures`) whose entries are this dimension's values */
  parameter?: string;
  values: DimensionValue[];
  /** the procedure's other entries: offered by `parameter`, not selected for this run (filled in by `resolveDimensions`) */
  offered?: DimensionValue[];
}

export type Classification = Record<string, string | null>;

function normalizeValue(raw: unknown): DimensionValue | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const label = typeof r.label === 'string' ? r.label.trim() : '';
  if (!label) return null;
  const match = Array.isArray(r.match) ? r.match.filter((m): m is string => typeof m === 'string' && m.trim() !== '').map((m) => m.trim()) : typeof r.match === 'string' ? r.match.split(',').map((m) => m.trim()).filter(Boolean) : [];
  return { label, match };
}

export function normalizeDimensions(raw: unknown): Dimension[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: Dimension[] = [];
  for (const d of raw) {
    if (!d || typeof d !== 'object') continue;
    const r = d as Record<string, unknown>;
    const label = typeof r.label === 'string' ? r.label.trim() : '';
    let id = typeof r.id === 'string' && /^[a-z0-9_-]{1,40}$/i.test(r.id) ? r.id : label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `dimension-${out.length + 1}`;
    while (seen.has(id)) id = `${id}-x`;
    seen.add(id);
    const values = Array.isArray(r.values) ? r.values.map(normalizeValue).filter((x): x is DimensionValue => x !== null) : [];
    const dimension: Dimension = { id, label: label || id, values };
    if (typeof r.role === 'string' && r.role) dimension.role = r.role;
    if (typeof r.parameter === 'string' && r.parameter.trim()) dimension.parameter = r.parameter.trim();
    out.push(dimension);
  }
  return out;
}

/** A block property naming one of the report's dimensions by id. */
export const dimensionRef = (label: string, options: { help?: string; optional?: boolean } = {}) => v.custom<string>('dimension', () => '', (raw) => (typeof raw === 'string' ? raw : ''), { label, ...options });

const sameLabel = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The dimensions as one run has them: each one declared over a procedure
 * parameter takes the entries the run selected as its values (after any it
 * declares), and the procedure's other entries as `offered`.
 */
export function resolveDimensions(dimensions: Dimension[], run: Pick<RunSnapshot, 'parameters'>): Dimension[] {
  return dimensions.map((d) => {
    if (!d.parameter) return d;
    const p = (run.parameters ?? []).find((x) => sameIdentifier(x.identifier, d.parameter as string));
    if (!p) return d;
    const known = (labels: string[]) => labels.map((x) => x.trim()).filter(Boolean);
    const selected = known(p.values.map(printValue));
    const values = [...d.values, ...selected.filter((x) => !d.values.some((v) => sameLabel(v.label, x))).map((label) => ({ label, match: [] }))];
    const offered = known((p.choices ?? []).map(printValue)).filter((x) => !values.some((v) => sameLabel(v.label, x))).map((label) => ({ label, match: [] }));
    return { ...d, values, offered };
  });
}

export const dimensionsField = (options: { label?: string; help?: string; default?: Dimension[] } = {}) =>
  v.custom<Dimension[]>('dimensions', () => normalizeDimensions(options.default ?? []), normalizeDimensions, { label: options.label ?? 'Test matrix dimensions', help: options.help ?? 'What the procedure repeats its steps over, and the words that name each value in a step title.' });

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const mentions = (text: string, word: string) => new RegExp(`(^|[^a-z0-9])${escape(word.toLowerCase())}($|[^a-z0-9])`).test(text.toLowerCase());

/** The value a step has for one dimension: a reading of the dimension's role on the step (or on the run), else the first value named in the step's title or path. */
export function classifyStep(step: RunStep, dimension: Dimension, run?: RunSnapshot): string | null {
  if (dimension.role) {
    const ids = roleSpec(dimension.role)?.identifiers ?? [];
    const plays = (r: RunReading) => ids.some((id) => sameIdentifier(r.identifier, id));
    const own = step.readings.find(plays);
    const anywhere = own ?? run?.steps.flatMap((s) => s.readings).find(plays);
    const value = anywhere ? firstValue(anywhere) : null;
    if (value !== null) {
      const text = String(value);
      const known = dimension.values.find((d) => [d.label, ...d.match].some((m) => m.toLowerCase() === text.trim().toLowerCase()));
      return known?.label ?? text;
    }
  }
  const texts = [step.title, ...step.path.slice().reverse()];
  const all = [...dimension.values, ...(dimension.offered ?? [])];
  for (const text of texts) {
    // a name the step quotes is the value it is at: "Region 1" in `in "Region 1" (OC1 & t1)`
    for (const m of Array.from(text.matchAll(/["“”]([^"“”]{1,60})["“”]/g))) {
      const quoted = all.find((value) => [value.label, ...value.match].some((word) => word && sameLabel(word, m[1])));
      if (quoted) return quoted.label;
    }
    // else the longest matching word wins, so "High Beam" is not read as "Beam"
    let best: { label: string; length: number } | null = null;
    for (const value of all) {
      for (const word of [value.label, ...value.match]) {
        if (word && mentions(text, word) && (!best || word.length > best.length)) best = { label: value.label, length: word.length };
      }
    }
    if (best) return best.label;
  }
  return null;
}

export function classify(step: RunStep, dimensions: Dimension[], run?: RunSnapshot): Classification {
  return Object.fromEntries(dimensions.map((d) => [d.id, classifyStep(step, d, run)]));
}

const CORNER_WORDS: Array<{ label: string; match: string[] }> = [
  { label: 'Minimum', match: ['min', 'cold'] },
  { label: 'Nominal', match: ['nom', 'room', 'ambient'] },
  { label: 'Maximum', match: ['max', 'hot'] },
];

/**
 * Dimensions a run's steps look like they repeat over: quoted names in step
 * titles ("Low Beam", "DRL") as one dimension, and minimum / nominal / maximum
 * with the quantity that follows ("at minimum voltage") as another. A proposal
 * for a report's author, never applied on its own.
 */
export function proposeDimensions(steps: RunStep[]): Dimension[] {
  const quoted = new Map<string, number>();
  const corners = new Map<string, Set<string>>();
  for (const step of steps) {
    const text = [step.title, ...step.path].join(' · ');
    for (const m of Array.from(text.matchAll(/["“”']([^"“”']{2,40})["“”']/g))) quoted.set(m[1].trim(), (quoted.get(m[1].trim()) ?? 0) + 1);
    for (const m of Array.from(text.matchAll(/\b(minimum|nominal|maximum|min|nom|max)\s+(voltage|temperature|supply|current|load)\b/gi))) {
      const quantity = m[2].toLowerCase();
      const corner = CORNER_WORDS.find((c) => c.label.toLowerCase() === m[1].toLowerCase() || c.match.includes(m[1].toLowerCase()))?.label;
      if (corner) corners.set(quantity, new Set([...(corners.get(quantity) ?? []), corner]));
    }
  }
  const out: Dimension[] = [];
  const names = Array.from(quoted.keys()).filter((k) => (quoted.get(k) ?? 0) >= 2);
  if (names.length >= 2) out.push({ id: 'function', label: 'Function', role: 'condition.function', values: names.map((label) => ({ label, match: [] })) });
  for (const [quantity, found] of Array.from(corners.entries())) {
    if (found.size < 2) continue;
    const label = quantity.charAt(0).toUpperCase() + quantity.slice(1);
    out.push({
      id: quantity,
      label,
      role: quantity === 'temperature' ? 'condition.temperature' : quantity === 'voltage' || quantity === 'supply' ? 'condition.supply' : undefined,
      values: CORNER_WORDS.filter((c) => found.has(c.label)).map((c) => ({ label: c.label, match: [`${c.label.toLowerCase()} ${quantity}`, ...c.match.map((m) => `${m} ${quantity}`)] })),
    });
  }
  return out;
}
